<template>
  <div class="resources-page">
    <!-- Header -->
    <div class="page-header">
      <button class="back-button" @click="goBack">
        <span class="back-arrow">←</span>
        Retour
      </button>

      <div class="header-content">
        <h1 class="page-title">
          <span class="title-icon">📚</span>
          Ressources & Aide
        </h1>
        <p class="page-subtitle">
          Bienvenue dans l'espace Ressources & Aide 🌱<br>
          Ici, vous trouverez tout ce dont vous avez besoin pour bien accompagner un jeune avec Autrement Cap'.
          Des tutoriels, des guides, et une communauté pour vous soutenir pas à pas.
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="resources-content">

      <!-- SECTION 1: Aide à l'utilisation -->
      <div class="main-section-divider">
        <h2 class="main-section-title">📱 Aide à l'utilisation de la plateforme</h2>
        <p class="main-section-desc">Tout ce qu'il faut savoir pour maîtriser l'application technique.</p>
      </div>

      <!-- FAQ Section -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Foire aux questions
        </h2>

        <div class="faq-categories">
          <!-- Utilisation de l'application -->
          <div class="faq-category">
            <h3 class="category-title">
              <span class="category-icon">💬</span>
              Utilisation de l'application
            </h3>
            <div class="faq-items">
              <div
                v-for="(item, index) in faqUsage"
                :key="index"
                class="faq-item"
                :class="{ 'active': activeFaq === `usage-${index}` }"
                @click="toggleFaq(`usage-${index}`)"
              >
                <div class="faq-question">
                  <span>{{ item.question }}</span>
                  <span class="faq-toggle">{{ activeFaq === `usage-${index}` ? '−' : '+' }}</span>
                </div>
                <div v-if="activeFaq === `usage-${index}`" class="faq-answer">
                  {{ item.answer }}
                </div>
              </div>
            </div>
          </div>

          <!-- Observation et compétences -->
          <div class="faq-category">
            <h3 class="category-title">
              <span class="category-icon">🧠</span>
              Observation et compétences
            </h3>
            <div class="faq-items">
              <div
                v-for="(item, index) in faqObservation"
                :key="index"
                class="faq-item"
                :class="{ 'active': activeFaq === `observation-${index}` }"
                @click="toggleFaq(`observation-${index}`)"
              >
                <div class="faq-question">
                  <span>{{ item.question }}</span>
                  <span class="faq-toggle">{{ activeFaq === `observation-${index}` ? '−' : '+' }}</span>
                </div>
                <div v-if="activeFaq === `observation-${index}`" class="faq-answer">
                  {{ item.answer }}
                </div>
              </div>
            </div>
          </div>

          <!-- Données et sécurité -->
          <div class="faq-category">
            <h3 class="category-title">
              <span class="category-icon">🔐</span>
              Données et sécurité
            </h3>
            <div class="faq-items">
              <div
                v-for="(item, index) in faqSecurity"
                :key="index"
                class="faq-item"
                :class="{ 'active': activeFaq === `security-${index}` }"
                @click="toggleFaq(`security-${index}`)"
              >
                <div class="faq-question">
                  <span>{{ item.question }}</span>
                  <span class="faq-toggle">{{ activeFaq === `security-${index}` ? '−' : '+' }}</span>
                </div>
                <div v-if="activeFaq === `security-${index}`" class="faq-answer">
                  {{ item.answer }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Glossaire -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Glossaire technique
        </h2>
        <div class="glossary-grid">
          <div v-for="(term, index) in glossaryTerms" :key="index" class="glossary-card">
            <h3 class="glossary-term">{{ term.term }}</h3>
            <p class="glossary-definition">{{ term.definition }}</p>
          </div>
        </div>
      </section>

      <!-- Vidéos tutorielles -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Tutoriels vidéo (Application)
        </h2>
        <div class="videos-grid">
          <div v-for="(video, index) in tutorialVideos" :key="index" class="video-card">
            <div class="video-thumbnail">
              <span class="video-icon">🎬</span>
              <div class="video-play">▶</div>
            </div>
            <h3 class="video-title">{{ video.title }}</h3>
            <p class="video-description">{{ video.description }}</p>
          </div>
        </div>
      </section>

      <!-- Contact & Support -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Support Technique
        </h2>
        <div class="contact-grid">
          <div class="contact-card">
            <div class="contact-icon">📧</div>
            <h3>Email de support</h3>
            <a href="mailto:support@autrementcap.fr" class="contact-link">
              support@autrementcap.fr
            </a>
          </div>
          <div class="contact-card">
            <div class="contact-icon">💬</div>
            <h3>Formulaire de signalement</h3>
            <p class="contact-description">
              Un bug ou un problème technique ?
            </p>
            <button class="contact-button" disabled>
              Signaler un problème
            </button>
          </div>
        </div>
      </section>

      <!-- SECTION 2: Accompagnement & Ressources -->
      <div class="main-section-divider" style="margin-top: 6rem;">
        <h2 class="main-section-title">🤝 Accompagnement & Ressources</h2>
        <p class="main-section-desc">Des outils et services pour vous aider dans votre mission d'accompagnant.</p>
      </div>

      <!-- Services Coaching -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Coaching & Formation
        </h2>
        <div class="resources-grid">
           <div v-for="(service, index) in coachingServices" :key="index" class="resource-service-card">
             <div class="service-icon">{{ service.icon }}</div>
             <h3 class="service-title">{{ service.title }}</h3>
             <p class="service-desc">{{ service.description }}</p>
             <span class="service-status">{{ service.status }}</span>
           </div>
        </div>
      </section>

      <!-- Communauté Parents -->
      <section class="content-section community-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Communauté
        </h2>
        <div class="community-card">
          <div class="community-icon">👨‍👩‍👧‍👦</div>
          <h3>Groupe Parents</h3>
          <p>
            Rejoignez un espace d'échange bienveillant dédié aux parents et aidants.
            Partagez vos expériences, vos doutes et vos victoires.
          </p>
          <button class="community-btn" disabled>Rejoindre le groupe (Bientôt)</button>
        </div>
      </section>

      <!-- Ressources Métiers -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Orientation & Métiers
        </h2>
        <div class="videos-grid">
           <!-- Placeholder for Job Videos -->
           <div class="video-card job-video-card">
            <div class="video-thumbnail job-thumbnail">
              <span class="video-icon">🎥</span>
              <div class="video-play">▶</div>
            </div>
            <h3 class="video-title">Vidéothèque des Métiers</h3>
            <p class="video-description">Découvrez des centaines de vidéos pour explorer les métiers et inspirer les jeunes.</p>
            <div class="coming-soon-overlay">Bientôt disponible</div>
          </div>
        </div>
      </section>

      <!-- Guides pratiques -->
      <section class="content-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Guides pratiques à télécharger
        </h2>
        <div class="guides-grid">
          <div v-for="(guide, index) in practicalGuides" :key="index" class="guide-card">
            <div class="guide-icon">📄</div>
            <div class="guide-info">
              <h3 class="guide-title">{{ guide.title }}</h3>
              <p class="guide-description">{{ guide.description }}</p>
            </div>
            <button class="guide-download" disabled>
              <span>Bientôt</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Encouragement final -->
      <section class="encouragement-section">
        <div class="encouragement-card">
          <h2>Chaque observation compte 🧡</h2>
          <p>
            Grâce à vous, un jeune progresse, se découvre et construit son avenir.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResourcesPage',
  mounted() {
    window.scrollTo(0, 0);
  },
  data() {
    return {
      activeFaq: null,
      faqUsage: [
        {
          question: "Comment créer mon profil d'aidant ?",
          answer: "Pour créer votre profil d'aidant, rendez-vous sur la page d'inscription accompagnateur. Renseignez vos informations personnelles, votre rôle (parent, enseignant, psychologue) et validez votre email. Vous pourrez ensuite vous connecter et personnaliser votre profil."
        },
        {
          question: "Comment ajouter un jeune à mon équipe ?",
          answer: "Sur votre tableau de bord, cliquez sur le bouton 'Ajouter un jeune' ou sur la carte '+'. Saisissez le code d'invitation unique du jeune (format : JEUNE-ABC123). Le jeune devra accepter votre demande avant que vous puissiez accéder à son profil et commencer à l'accompagner."
        },
        {
          question: "Comment enregistrer une observation ?",
          answer: "Accédez au profil du jeune que vous accompagnez, puis cliquez sur 'Ajouter une observation'. Décrivez de manière factuelle et objective une situation où vous avez observé le jeune mobiliser une compétence. Précisez le contexte, l'action réalisée et le résultat obtenu."
        },
        {
          question: "Puis-je modifier une observation après l'avoir validée ?",
          answer: "Oui, vous pouvez modifier une observation après validation. Accédez à l'observation depuis le profil du jeune, cliquez sur 'Modifier' et apportez vos changements. Gardez à l'esprit que toutes les modifications sont tracées pour garantir la transparence."
        }
      ],
      faqObservation: [
        {
          question: "Comment savoir si ce que j'ai observé est une \"compétence\" ?",
          answer: "Une compétence est une capacité mise en œuvre dans une situation concrète. Posez-vous la question : le jeune a-t-il réussi à faire quelque chose ? A-t-il mobilisé des connaissances, un savoir-faire ou un comportement adapté ? Si oui, c'est une compétence observable. Concentrez-vous sur les faits, pas sur les intentions."
        },
        {
          question: "À quelle fréquence dois-je observer ?",
          answer: "Il n'y a pas de fréquence imposée. L'important est la qualité de l'observation plutôt que la quantité. Observez régulièrement, dans différents contextes (classe, atelier, sortie...) pour avoir une vision complète du jeune. Une à deux observations par semaine est un bon rythme."
        },
        {
          question: "Que faire si je ne suis pas sûr de mon observation ?",
          answer: "C'est normal d'avoir des doutes ! Vous pouvez échanger avec d'autres membres de l'équipe accompagnante du jeune, consulter le glossaire des compétences, ou contacter notre support. L'observation est un apprentissage continu, et vos compétences s'affineront avec la pratique."
        }
      ],
      faqSecurity: [
        {
          question: "Qui a accès aux données du jeune ?",
          answer: "Seuls les accompagnateurs autorisés par le jeune (ou son représentant légal) ont accès aux données. Chaque membre de l'équipe a un rôle défini (parent, enseignant, psychologue) et peut consulter les observations et le profil du jeune. Les données sont hébergées de manière sécurisée et conforme au RGPD."
        },
        {
          question: "Puis-je supprimer mon compte ou mes observations ?",
          answer: "Oui, vous pouvez supprimer votre compte à tout moment depuis vos paramètres. Vous pouvez également supprimer vos observations individuelles. Attention : la suppression d'une observation est définitive et impactera le suivi du jeune. En cas de doute, contactez notre support."
        }
      ],
      glossaryTerms: [
        {
          term: "TND",
          definition: "Troubles Neurodéveloppementaux : ensemble de troubles affectant le développement cognitif, comme l'autisme ou la dyslexie."
        },
        {
          term: "Badge",
          definition: "Symbole visuel regroupant plusieurs compétences observables. Les badges permettent de valoriser les progrès du jeune de manière ludique."
        },
        {
          term: "Observation",
          definition: "Description factuelle d'une situation réelle où le jeune a mobilisé une compétence. Une bonne observation est précise, objective et contextualisée."
        },
        {
          term: "Contexte sensoriel",
          definition: "Environnement dans lequel le jeune réussit le mieux (bruit, lumière, interactions...). Identifier ces contextes permet d'optimiser l'accompagnement."
        },
        {
          term: "Compétence transférable",
          definition: "Compétence mobilisable dans différents contextes (école, maison, travail). Ces compétences sont particulièrement valorisées dans l'insertion professionnelle."
        },
        {
          term: "Accompagnateur",
          definition: "Personne qui observe et soutient le jeune dans son parcours : parent, enseignant, éducateur, psychologue, etc."
        }
      ],
      tutorialVideos: [
        {
          title: "Découvrir l'interface en 2 minutes",
          description: "Une visite guidée rapide pour prendre en main l'application"
        },
        {
          title: "Faire ma première observation",
          description: "Apprenez à observer et enregistrer les compétences d'un jeune"
        },
        {
          title: "Comprendre le système de badges",
          description: "Comment les badges valorisent les progrès du jeune"
        },
        {
          title: "Partager avec l'équipe du jeune",
          description: "Collaborez efficacement avec les autres accompagnateurs"
        }
      ],
      practicalGuides: [
        {
          title: "Guide d'observation des compétences",
          description: "Méthodes et exemples concrets pour observer efficacement"
        },
        {
          title: "Bonnes pratiques pour observer sans juger",
          description: "Comment rester objectif et bienveillant dans vos observations"
        },
        {
          title: "Guide du profil aidant",
          description: "Configurez et optimisez votre profil accompagnateur étape par étape"
        }
      ],
      coachingServices: [
        {
          title: "Visio / Coaching en ligne",
          description: "Des sessions en visio pour répondre à vos questions et vous guider.",
          icon: "💻",
          status: "Bientôt"
        },
        {
          title: "Coaching individuel",
          description: "Un accompagnement sur-mesure avec un expert pour vous et votre jeune.",
          icon: "🤝",
          status: "Bientôt"
        },
        {
          title: "Bootcamp",
          description: "Formation intensive de plusieurs jours pour devenir un expert de l'accompagnement.",
          icon: "🚀",
          status: "Bientôt"
        }
      ]
    };
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    toggleFaq(faqId) {
      if (this.activeFaq === faqId) {
        this.activeFaq = null;
      } else {
        this.activeFaq = faqId;
      }
    }
  }
};
</script>

<style scoped>
.resources-page {
  min-height: 100vh;
  background: linear-gradient(to bottom, #F9FAFB 0%, #FFFFFF 100%);
  padding: 2rem;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto 3rem;
}

.back-button {
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.back-button:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.back-arrow {
  font-size: 1.25rem;
}

.header-content {
  text-align: center;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.title-icon {
  font-size: 3rem;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #6B7280;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
}

.resources-content {
  max-width: 1200px;
  margin: 0 auto;
}

.main-section-divider {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 1rem;
  border-bottom: 2px dashed #E5E7EB;
}

.main-section-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 0.5rem 0;
}

.main-section-desc {
  font-size: 1.1rem;
  color: #6B7280;
  margin: 0;
}

.content-section {
  margin-bottom: 4rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.title-decoration {
  width: 4px;
  height: 32px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 2px;
}

/* FAQ Styles */
.faq-categories {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.faq-category {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.category-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.category-icon {
  font-size: 1.5rem;
}

.faq-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.faq-item {
  background: #F9FAFB;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.faq-item:hover {
  background: #F3F4F6;
}

.faq-item.active {
  background: #FFF5F0;
  border: 2px solid #FF9800;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  font-weight: 600;
  color: #111827;
  gap: 1rem;
}

.faq-toggle {
  font-size: 1.5rem;
  font-weight: 300;
  color: #6B7280;
  flex-shrink: 0;
}

.faq-item.active .faq-toggle {
  color: #FF9800;
}

.faq-answer {
  padding: 0 1.5rem 1.25rem;
  color: #4B5563;
  line-height: 1.7;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Glossary Styles */
.glossary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.glossary-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.glossary-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.glossary-term {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
}

.glossary-definition {
  color: #4B5563;
  line-height: 1.6;
  margin: 0;
}

/* Videos Styles */
.videos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.video-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.video-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.video-thumbnail {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.job-thumbnail {
  background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
}

.video-icon {
  font-size: 3rem;
}

.video-play {
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #FF9800;
  padding-left: 4px;
}

.job-thumbnail .video-play {
  color: #4F46E5;
}

.video-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 1rem 1rem 0.5rem;
}

.video-description {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0 1rem 1rem;
  line-height: 1.5;
}

.coming-soon-overlay {
  background: #F3F4F6;
  color: #6B7280;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Resources Grid */
.resources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.resource-service-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.resource-service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.service-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.service-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
}

.service-desc {
  color: #6B7280;
  font-size: 0.9375rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.service-status {
  background: #FEF3C7;
  color: #D97706;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: 99px;
  text-transform: uppercase;
}

/* Guides Styles */
.guides-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.guide-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.3s ease;
}

.guide-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.guide-icon {
  font-size: 3rem;
  flex-shrink: 0;
}

.guide-info {
  flex: 1;
}

.guide-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.guide-description {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
}

.guide-download {
  background: #F3F4F6;
  color: #9CA3AF;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  flex-shrink: 0;
}

/* Contact Styles */
.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.contact-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
}

.contact-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.contact-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.contact-card h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1rem 0;
}

.contact-link {
  display: inline-block;
  color: #FF9800;
  font-weight: 600;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  background: #FFF5F0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.contact-link:hover {
  background: #FFEDD5;
}

.contact-description {
  color: #6B7280;
  margin: 0 0 1rem 0;
}

.contact-button {
  background: #F3F4F6;
  color: #9CA3AF;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
}

/* Community Styles */
.community-section {
  margin-bottom: 3rem;
}

.community-card {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  color: white;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.community-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.2);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.community-card h3 {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
}

.community-card p {
  font-size: 1.1rem;
  margin: 0 0 1.5rem 0;
  opacity: 0.95;
  max-width: 600px;
  line-height: 1.6;
}

.community-btn {
  background: white;
  color: #FF9800;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: not-allowed;
  opacity: 0.9;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Encouragement Styles */
.encouragement-section {
  margin-bottom: 2rem;
}

.encouragement-card {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  text-align: center;
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.encouragement-card h2 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.encouragement-card p {
  font-size: 1rem;
  margin: 0;
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
  .resources-page {
    padding: 1rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .glossary-grid,
  .videos-grid,
  .contact-grid,
  .resources-grid {
    grid-template-columns: 1fr;
  }

  .guide-card {
    flex-direction: column;
    text-align: center;
  }

  .faq-category {
    padding: 1.5rem;
  }
}
</style>
